import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, LogOut, RefreshCw } from 'lucide-react';
import { toast } from '../utils/feedback';

const VerificationScreen = () => {
  const { currentUser, logout, sendVerification } = useAuth();
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.classList.add('auth-bg');
    document.body.classList.add('auth-bg');
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.classList.add('full-width');
    
    return () => {
      document.documentElement.classList.remove('auth-bg');
      document.body.classList.remove('auth-bg');
      if (rootEl) rootEl.classList.remove('full-width');
    };
  }, []);

  const handleResend = async () => {
    if (!currentUser) return;
    setIsResending(true);
    try {
      await sendVerification(currentUser);
      toast.success("Un nouvel e-mail a été envoyé. Vérifie tes spams.");
    } catch (error) {
      console.error(error);
      if (error.code === 'auth/too-many-requests') {
        toast.error("Patiente quelques instants avant de renvoyer un e-mail.");
      } else {
        toast.error("Erreur lors de l'envoi de l'e-mail.");
      }
    }
    setIsResending(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleRefresh = async () => {
    if (currentUser) {
      await currentUser.reload();
      window.location.reload();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="auth-page-wrapper" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      animation: 'fadeIn 0.5s ease-out',
      overflowY: 'auto',
      boxSizing: 'border-box'
    }}>
      <style>{`
        .verif-card {
          background-color: var(--card, #1c1a27);
          border-radius: 22px;
          padding: 2rem 1.8rem;
          max-width: 440px;
          width: 100%;
          margin: auto;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          border: 1px solid var(--border, rgba(255,255,255,0.08));
          position: relative;
          box-sizing: border-box;
        }
        @media (max-width: 600px) {
          .verif-card {
            padding: 1.5rem 1.2rem !important;
            border-radius: 18px !important;
            max-width: 100% !important;
          }
          .verif-card .verif-icon-wrap {
            width: 60px !important;
            height: 60px !important;
            margin-bottom: 0.85rem !important;
          }
          .verif-card .verif-icon-wrap svg {
            width: 30px !important;
            height: 30px !important;
          }
          .verif-card h2 {
            font-size: 1.35rem !important;
            margin-bottom: 0.6rem !important;
          }
          .verif-card p {
            font-size: 0.85rem !important;
            margin-bottom: 1.2rem !important;
          }
          .verif-card .btn {
            padding: 0.65rem !important;
            font-size: 0.92rem !important;
          }
        }
      `}</style>

      {/* Decorative background glow blobs */}
      <div style={{ position: 'fixed', top: '10%', left: '20%', width: 'min(300px, 60vw)', height: 'min(300px, 60vw)', background: 'var(--primary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '10%', right: '20%', width: 'min(250px, 50vw)', height: 'min(250px, 50vw)', background: 'var(--secondary-color)', borderRadius: '50%', filter: 'blur(100px)', opacity: 'var(--blob-opacity)', pointerEvents: 'none' }} />

      <div className="verif-card">
        {/* Shimmer effect line */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, var(--primary-color), transparent)', opacity: 0.5 }} />
        
        <div className="verif-icon-wrap" style={{ 
          width: '72px', height: '72px', borderRadius: '50%', 
          backgroundColor: 'rgba(108, 92, 231, 0.1)', 
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.2rem',
          color: 'var(--primary-color)'
        }}>
          <Mail size={36} />
        </div>

        <h2 style={{ 
          fontSize: '1.5rem', 
          background: 'linear-gradient(to right, var(--text-primary), var(--text-secondary))', 
          WebkitBackgroundClip: 'text', 
          WebkitTextFillColor: 'transparent',
          margin: '0 0 0.8rem 0' 
        }}>
          Vérifie ton e-mail
        </h2>
        
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.5', fontSize: '0.9rem' }}>
          Nous avons envoyé un lien de confirmation à <strong style={{ color: 'var(--text-primary)' }}>{currentUser?.email}</strong>.<br/>
          Clique sur ce lien pour activer ton compte.<br/>
          <span style={{ fontSize: '0.82rem', color: 'var(--danger-color)', display: 'block', marginTop: '0.4rem' }}>
            (Pense à vérifier tes courriers indésirables / spams)
          </span>
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button 
            className="btn btn-primary" 
            style={{ 
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.7rem',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '10px',
              fontWeight: 'bold',
              fontSize: '0.96rem',
              background: 'linear-gradient(135deg, var(--primary-color), #8c7fbf)',
              boxShadow: '0 4px 15px rgba(168, 159, 207, 0.3)'
            }}
            onClick={handleRefresh}
          >
            <RefreshCw size={18} /> J'ai cliqué sur le lien (Actualiser)
          </button>

          <button 
            className="btn" 
            style={{ 
              backgroundColor: 'var(--glass-input-bg)', 
              color: 'var(--text-primary)', 
              border: '1px solid var(--border-color)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.68rem',
              cursor: 'pointer',
              borderRadius: '10px',
              fontSize: '0.92rem'
            }}
            onClick={handleResend}
            disabled={isResending}
          >
            <Mail size={18} /> {isResending ? 'Envoi en cours...' : "Renvoyer l'e-mail"}
          </button>

          <button 
            className="btn" 
            style={{ 
              backgroundColor: 'transparent', 
              color: 'var(--danger-color)', 
              border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
              padding: '0.5rem',
              marginTop: '0.3rem',
              cursor: 'pointer',
              borderRadius: '10px',
              fontSize: '0.88rem'
            }}
            onClick={handleLogout}
          >
            <LogOut size={18} /> Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationScreen;
